/**
 * Which partners the report page's Partners tab lists, and the context
 * line each of their assignments carries: data in, data out.
 */

import { isRegistryPartner, type NetworkPartnerSummary } from '@/types/network-partner';
import {
  describeManagedFee,
  describeRegistryOrderCount,
  isManagedAssignment,
  isRegistryPartnerAssignment,
  type BackofficeAssignment,
} from '@/types/np-assignments';
import { formatGbp } from '@/types/registry-documents';

export interface DispatchedPartner {
  npId: string;
  networkPartner: NetworkPartnerSummary;
  /** The listed assignments this NP is locked on (dispatched, or
   * Armadillo's locked ones) — context lines. */
  assignments: BackofficeAssignment[];
  /** "A2" for every assignment of the report, by id: how a taken-over
   * automatic assignment names the manual one that took it over. */
  assignmentLabels: Readonly<Record<string, string>>;
}

/** Whether an assignment puts its partner on the Partners tab: it reached
 * Backoffice, or it is Armadillo's and locked. Armadillo's automatic
 * assignment may never be sent (shadow mode, no project yet) and a manual
 * one locked in triage is not sent before Start, yet the conversation is
 * there to read from the first order. */
function isListed(assignment: BackofficeAssignment): boolean {
  if (!assignment.networkPartnerId) {
    return false;
  }
  if (assignment.status === 'sent_to_backoffice') {
    return true;
  }
  return assignment.status === 'np_locked' && isRegistryPartnerAssignment(assignment);
}

/** The partner an assignment is locked on, from its own summary or else
 * from its shortlist. */
function lockedPartnerOf(assignment: BackofficeAssignment): NetworkPartnerSummary | null {
  return (
    assignment.networkPartner ??
    assignment.shortlist.find((s) => s.npId === assignment.networkPartnerId)?.networkPartner ??
    null
  );
}

/**
 * One entry per NP locked on a dispatched assignment, and Armadillo from a
 * locked one (`isListed`). An NP locked on two assignments of the same
 * report appears once — the chat thread is per-NP, not per-assignment.
 */
export function groupDispatchedPartners(assignments: BackofficeAssignment[]): DispatchedPartner[] {
  const assignmentLabels = Object.fromEntries(assignments.map((a, i) => [a.id, `A${i + 1}`]));
  const byNp = new Map<string, DispatchedPartner>();
  for (const assignment of assignments) {
    const summary = isListed(assignment) ? lockedPartnerOf(assignment) : null;
    if (!summary || !assignment.networkPartnerId) {
      continue;
    }
    const existing = byNp.get(assignment.networkPartnerId);
    if (existing) {
      existing.assignments.push(assignment);
    } else {
      byNp.set(assignment.networkPartnerId, {
        npId: assignment.networkPartnerId,
        networkPartner: summary,
        assignments: [assignment],
        assignmentLabels,
      });
    }
  }
  return [...byNp.values()];
}

/** Whether a listed partner is Armadillo: the partner record this
 * environment names, or the one an automatic assignment bills. The second
 * holds when the setting was cleared or changed after the automatic
 * assignment was made: the row stays listed, and must still read as
 * Armadillo's conversation rather than as a Freshdesk thread. */
export function isRegistryPartnerEntry(
  partner: Pick<DispatchedPartner, 'networkPartner' | 'assignments'>
): boolean {
  return isRegistryPartner(partner.networkPartner) || partner.assignments.some(isManagedAssignment);
}

/** The entry for Armadillo, when it is listed. */
export function registryPartnerOf(
  partners: readonly DispatchedPartner[]
): DispatchedPartner | null {
  return partners.find(isRegistryPartnerEntry) ?? null;
}

/** Armadillo's automatic assignment: its orders and fee, or which manual
 * assignment took it over. */
function managedContext(
  assignment: BackofficeAssignment,
  labels: Readonly<Record<string, string>>
): string {
  if (assignment.takenOverById !== null) {
    const by = labels[assignment.takenOverById] ?? 'a manual assignment';
    return `Automatic · taken over by ${by}`;
  }
  return `${describeRegistryOrderCount(assignment)} · ${describeManagedFee(assignment)}`;
}

/** A manual Armadillo assignment holding orders: the fee the Hub keeps
 * from them (their total plus any adjustment), with a quote still pending
 * called out, as on the automatic assignment's line. */
function manualRegistryContext(assignment: BackofficeAssignment): string {
  if (assignment.npFee === null) {
    return `${describeRegistryOrderCount(assignment)} · no fee set`;
  }
  const pending = assignment.awaitingQuote ? ' + quote pending' : '';
  return `${describeRegistryOrderCount(assignment)} · fee ${formatGbp(assignment.npFee)}${pending}`;
}

/** "2 scope items · Kenya · dispatched 01/10/2026". */
function scopeContext(assignment: BackofficeAssignment): string {
  const jurisdictions = [
    ...new Set(
      assignment.items
        .map((i) => i.jurisdictionName)
        .filter((name): name is string => Boolean(name))
    ),
  ];
  const parts = [
    `${assignment.items.length} scope item${assignment.items.length === 1 ? '' : 's'}`,
  ];
  if (jurisdictions.length > 0) {
    parts.push(jurisdictions.join(', '));
  }
  if (assignment.dispatchedAt) {
    parts.push(`dispatched ${new Date(assignment.dispatchedAt).toLocaleDateString()}`);
  }
  return parts.join(' · ');
}

/** The line an assignment carries under its partner. */
export function assignmentContext(
  assignment: BackofficeAssignment,
  labels: Readonly<Record<string, string>> = {}
): string {
  if (isManagedAssignment(assignment)) {
    return managedContext(assignment, labels);
  }
  // The partner check first: an assignment off an older API carries no
  // registry fields at all.
  if (isRegistryPartner(assignment.networkPartner) && assignment.registryOrders.length > 0) {
    return manualRegistryContext(assignment);
  }
  return scopeContext(assignment);
}
